/* eslint-disable @typescript-eslint/no-explicit-any */
import {LoggedInUser, UserResponse} from '@api'
import {ContractStatus, ContractType, ContractVariant, LocalStorageItem, TranslationKey, UserRole} from '@enums'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {SvgIcon} from '@mui/material'

import {SyntheticEvent, useEffect, useRef} from 'react'

import html2canvas from 'html2canvas'
import {TFunction} from 'i18next'
import jsPDF from 'jspdf'

import {UIKit} from '@components'

import placeholder from '@images/placeholder.png'

import {icons} from './consts'

/**
 * A function that executes a passed function only once on component render.
 * @param loadData Function
 */
export function useLoadOnce(loadData: () => void): void {
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      loadData()
      isFirstRender.current = false
    }
  }, [loadData])
}

/**
 * @param type Type of Contract Variant
 * @param creditCheckPassed  Credit Check result
 * @returns An icon based on the Credit Check result and type of Contract Variant.
 */
export const getCreditCheckModalIcon = (
  type: ContractVariant,
  contractType: ContractType,
  creditCheckPassed: boolean,
) => {
  if (type === ContractVariant.QUOTE || contractType === ContractType.B2B)
    return <InfoOutlinedIcon style={{fontSize: 100}} />
  else if (creditCheckPassed) return <CheckCircleOutlineOutlinedIcon style={{fontSize: 100}} />
  return <ErrorOutlineOutlinedIcon style={{fontSize: 100}} />
}

/**
 * @param dataMatches  Document Upload result
 * @returns An icon based on the Document Upload result.
 */
export const getDocumentUploadModalIcon = (dataMatches: boolean) => {
  if (dataMatches) return <CheckCircleOutlineOutlinedIcon style={{fontSize: 100}} />
  return <ErrorOutlineOutlinedIcon style={{fontSize: 100}} />
}

/**
 * @param t Translation function
 * @param status Contract Status
 * @returns Readable text representing the Contract Status.
 */
export const getStatusText = (
  t: TFunction,
  status: ContractStatus | 'Active' | 'Terminated' | 'Cancelled' | 'Registration',
): string => {
  switch (status) {
    case ContractStatus.QUOTE:
      return t(TranslationKey.Quote)
    case ContractStatus.AWAITING_VERIFICATION:
      return t(TranslationKey['Pending'])
    case ContractStatus.DRAFT:
      return t(TranslationKey.Draft)
    case ContractStatus.SENT_FOR_SIGNING:
      return t(TranslationKey['Sent'])
    case ContractStatus.CANCELED:
      return t(TranslationKey.Cancelled)
    case ContractStatus.ACTIVE:
      return t(TranslationKey.Active)
    case ContractStatus.SIGNED:
      return t(TranslationKey.Signed)
    default:
      return status
  }
}

/**
 * @returns Logged in User
 */
export const getUserData = (): LoggedInUser =>
  JSON.parse(String(localStorage.getItem(LocalStorageItem.AUTHENTICATION)))?.userData || null

export const getToken = (): string =>
  JSON.parse(String(localStorage.getItem(LocalStorageItem.AUTHENTICATION)))?.token || null

export const setAuthenticationStorage = (data: string): void =>
  localStorage.setItem(LocalStorageItem.AUTHENTICATION, data)

export const refreshUserDataAuthStorage = (userData: UserResponse) => {
  const token = getToken()

  setAuthenticationStorage(
    JSON.stringify({
      token,
      userData,
    }),
  )
}

/**
 * @returns If the User is Super Administrator
 */
export const isSuperAdmin = () => {
  const currentUser = getUserData()
  return currentUser.role === UserRole.SUPER_ADMIN
}

/**
 * @returns If the User is Company Administrator
 */
export const isCompanyAdmin = () => {
  const currentUser = getUserData()
  return currentUser.role === UserRole.COMPANY_ADMIN
}
/**
 * @returns If the User is Company User
 */
export const isCompanyUser = () => {
  const currentUser = getUserData()
  return currentUser.role === UserRole.COMPANY_USER
}

/**
 * @returns If the User is Parent Company Administrator
 */
export const isParentAdminUser = () => {
  const currentUser = getUserData()
  return currentUser.role === UserRole.PARENT_ADMINISTRATOR
}

/**
 * @param amount Amount to be formatted
 * @returns Formatted amount
 */
export const formatCurrency = (amount: number | string, currency: string) => {
  const number = Number(amount)
  const nanGuard = isNaN(number) ? 0 : number
  return Intl.NumberFormat('de-DE', {style: 'currency', currency: currency ?? 'EUR'}).format(nanGuard)
}

/**
 * @param value Amount to be formatted
 * @returns Formatted amount
 */
export const formatPercentage = (value: number | string) => {
  const number = Number(value)
  const nanGuard = isNaN(number) ? 0 : number

  return Intl.NumberFormat('de-DE', {style: 'percent', minimumFractionDigits: 2}).format(nanGuard / 100)
}

/**
 * A function to generate DropdownItem array from a given array
 * @param items Data to be transformed into DropdownItems
 * @param labelProp Prop to be used for label
 * @param valueProp Prop to be used for value
 * @param iconProp Prop to be used for icon
 * @returns An array of DropdownItems
 */
export const generateDropdownOptions = <T extends Record<string, any>>(
  items: T[],
  labelProp?: keyof T,
  valueProp?: keyof T,
  iconProp?: keyof T,
): UIKit.DropdownItem[] =>
  items
    .filter(item => item.active)
    .map(item => ({
      label: String(item[labelProp ?? 'name']),
      value: String(item[valueProp ?? 'id']),
      ...(iconProp && {icon: <SvgIcon style={{width: 'auto', height: '1.2rem'}}> {icons[item[iconProp]]}</SvgIcon>}),
    }))

/**
 * Image fetch error fallback
 * @param e Error event
 */
export const onImageError = (e: SyntheticEvent<HTMLImageElement, Event>) => {
  const target = e.target as HTMLImageElement
  target.src = placeholder
  target.onerror = null
}

export async function inlineImages(html: string): Promise<string> {
  const tempContainer = document.createElement('div')
  tempContainer.innerHTML = html

  const imgs = Array.from(tempContainer.querySelectorAll<HTMLImageElement>('img'))
  for (const img of imgs) {
    try {
      const res = await fetch(img.src)
      const blob = await res.blob()
      const reader = new FileReader()
      const base64: string = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
      img.src = base64
    } catch (err) {
      console.warn('Could not inline image:', img.src)
    }
  }

  return tempContainer.innerHTML
}

/**
 * A function to generate a PDF from HTML
 * @param html HTML string
 * @returns PDF document
 */
export const generatePdf = async (html: string): Promise<jsPDF> => {
  const tempContainer = document.createElement('div')
  tempContainer.innerHTML = html
  tempContainer.style.position = 'absolute'
  tempContainer.style.left = '-9999px'
  tempContainer.style.top = '0'
  tempContainer.style.width = '210mm'
  document.body.appendChild(tempContainer)

  await new Promise(resolve => setTimeout(resolve, 100)) // let DOM render

  const doc = new jsPDF({unit: 'mm', format: 'a4', orientation: 'portrait'})
  const pdfWidth = doc.internal.pageSize.getWidth()
  const pdfHeight = doc.internal.pageSize.getHeight()
  const padding = 12

  const pageElements = Array.from(tempContainer.querySelectorAll<HTMLElement>('.page'))

  for (let i = 0; i < pageElements.length; i++) {
    const el = pageElements[i]

    const canvas = await html2canvas(el, {scale: 1, useCORS: true, logging: false})
    const imgData = canvas.toDataURL('image/jpeg', 0.7) // quality 0.7

    const imgProps = {width: canvas.width, height: canvas.height}
    const ratio = (pdfWidth - padding * 2) / imgProps.width // fit inside padded area
    const imgHeight = imgProps.height * ratio

    if (i > 0) doc.addPage()

    if (imgHeight <= pdfHeight - padding * 2) {
      // fits on one page with padding
      doc.addImage(imgData, 'PNG', padding, padding, pdfWidth - padding * 2, imgHeight)
    } else {
      // split into multiple pages
      let position = 0
      const pageCanvasHeight = (pdfHeight - padding * 2) / ratio

      while (position < canvas.height) {
        const pageCanvas = document.createElement('canvas')
        pageCanvas.width = canvas.width
        pageCanvas.height = Math.min(pageCanvasHeight, canvas.height - position)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const ctx = pageCanvas.getContext('2d')!
        ctx.drawImage(canvas, 0, position, canvas.width, pageCanvas.height, 0, 0, canvas.width, pageCanvas.height)
        const pageImg = pageCanvas.toDataURL('image/png')
        if (position > 0) doc.addPage()
        doc.addImage(pageImg, 'PNG', padding, padding, pdfWidth - padding * 2, pageCanvas.height * ratio)
        position += pageCanvas.height
      }
    }
  }

  document.body.removeChild(tempContainer)
  return doc
}

/**
 * Adds "Page X of Y" numbering at the top-right corner of each page in a jsPDF document.
 *
 * @param pdf - jsPDF instance (already generated PDF)
 * @param translatePageLabel - function to generate the page label, e.g. (i, total) => `Page ${i} of ${total}`
 */
export const addTopRightPageNumbers = (pdf: jsPDF, translatePageLabel: (page: number, total: number) => string) => {
  const totalPages = pdf.getNumberOfPages()
  const pdfWidth = pdf.internal.pageSize.getWidth()
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i)

    pdf.setFontSize(8)

    pdf.setTextColor(128, 128, 128)

    pdf.text(translatePageLabel(i, totalPages), pdfWidth - 12, 12, {align: 'right'})
  }

  return pdf
}
